/**
 * FairnessPanel — Fairness reports, model cards, protected attribute detection
 * Phase 4.8-B — WQ-198
 */
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  evaluateFairness, getFairnessReport, detectProtectedAttributes,
  generateModelCard, getModelCard, getModelCardMarkdown,
  type FairnessReport, type ProtectedAttributeDetection, type ModelCard,
} from '../../services/fairnessService';
import { fmt, fmtPct } from '../../utils/formatNumber';

const FairnessPanel: React.FC = () => {
  const { authFetch } = useAuth();
  const [activeView, setActiveView] = useState<'evaluate' | 'report' | 'model-card'>('evaluate');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Evaluate form state
  const [evalForm, setEvalForm] = useState({ model_id: '', dataset_id: '', protected_attributes: '' });
  const [report, setReport] = useState<FairnessReport | null>(null);
  const [detection, setDetection] = useState<ProtectedAttributeDetection | null>(null);

  // Model card state
  const [modelCardId, setModelCardId] = useState('');
  const [modelCard, setModelCard] = useState<ModelCard | null>(null);
  const [modelCardMd, setModelCardMd] = useState<string | null>(null);
  const [cardViewMode, setCardViewMode] = useState<'json' | 'markdown'>('json');

  const handleEvaluate = async () => {
    if (!evalForm.model_id || !evalForm.dataset_id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await evaluateFairness(authFetch, {
        model_id: evalForm.model_id,
        dataset_id: evalForm.dataset_id,
        protected_attributes: evalForm.protected_attributes.split(',').map(s => s.trim()).filter(Boolean),
      });
      setReport(result);
      setActiveView('report');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Evaluation failed');
    }
    setLoading(false);
  };

  const handleDetect = async () => {
    if (!evalForm.dataset_id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await detectProtectedAttributes(authFetch, { dataset_id: evalForm.dataset_id });
      setDetection(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Detection failed');
    }
    setLoading(false);
  };

  const handleLoadReport = async () => {
    if (!evalForm.model_id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getFairnessReport(authFetch, evalForm.model_id);
      setReport(result);
      setActiveView('report');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    }
    setLoading(false);
  };

  const handleLoadModelCard = async () => {
    if (!modelCardId) return;
    setLoading(true);
    setError(null);
    try {
      const [card, md] = await Promise.all([
        getModelCard(authFetch, modelCardId),
        getModelCardMarkdown(authFetch, modelCardId).catch(() => null),
      ]);
      setModelCard(card);
      setModelCardMd(md);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load model card');
    }
    setLoading(false);
  };

  const handleGenerateCard = async () => {
    if (!modelCardId) return;
    setLoading(true);
    setError(null);
    try {
      const card = await generateModelCard(authFetch, { model_id: modelCardId, include_fairness: true });
      setModelCard(card);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    }
    setLoading(false);
  };

  return (
    <div>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([
          { key: 'evaluate', label: 'Evaluate Fairness' },
          { key: 'report', label: 'View Report' },
          { key: 'model-card', label: 'Model Cards' },
        ] as const).map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)} style={{
            fontSize: 13, padding: '6px 16px', borderRadius: 6,
            border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 500,
            background: activeView === v.key ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: activeView === v.key ? '#fff' : 'var(--text-primary)',
          }}>
            {v.label}
          </button>
        ))}
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Evaluate view */}
      {activeView === 'evaluate' && (
        <div style={{ padding: 16, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Run Fairness Evaluation</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Model ID *</label>
              <input
                value={evalForm.model_id}
                onChange={e => setEvalForm(p => ({ ...p, model_id: e.target.value }))}
                placeholder="Enter model ID"
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Dataset ID *</label>
              <input
                value={evalForm.dataset_id}
                onChange={e => setEvalForm(p => ({ ...p, dataset_id: e.target.value }))}
                placeholder="Enter dataset ID"
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13 }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                Protected Attributes (comma-separated)
              </label>
              <input
                value={evalForm.protected_attributes}
                onChange={e => setEvalForm(p => ({ ...p, protected_attributes: e.target.value }))}
                placeholder="e.g. gender, race, age"
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 13 }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={handleEvaluate} disabled={loading || !evalForm.model_id || !evalForm.dataset_id} style={{
              fontSize: 13, padding: '6px 20px', borderRadius: 6, border: 'none',
              fontWeight: 600, cursor: 'pointer', background: '#22c55e', color: '#fff',
              opacity: (!evalForm.model_id || !evalForm.dataset_id || loading) ? 0.5 : 1,
            }}>
              {loading ? 'Evaluating...' : 'Evaluate'}
            </button>
            <button onClick={handleDetect} disabled={loading || !evalForm.dataset_id} style={{
              fontSize: 13, padding: '6px 16px', borderRadius: 6,
              border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
              background: 'var(--bg-primary)', color: 'var(--text-primary)',
              opacity: (!evalForm.dataset_id || loading) ? 0.5 : 1,
            }}>
              Detect Protected Attributes
            </button>
            <button onClick={handleLoadReport} disabled={loading || !evalForm.model_id} style={{
              fontSize: 13, padding: '6px 16px', borderRadius: 6,
              border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
              background: 'var(--bg-primary)', color: 'var(--text-primary)',
              opacity: (!evalForm.model_id || loading) ? 0.5 : 1,
            }}>
              Load Existing Report
            </button>
          </div>

          {/* Detection results */}
          {detection && (
            <div style={{ marginTop: 16, padding: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Detected Protected Attributes</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {detection.detected_attributes.map(attr => (
                  <span key={attr} style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 12,
                    background: 'rgba(168,85,247,0.1)', color: '#a855f7',
                  }}>
                    {attr} ({((detection.confidence[attr] || 0) * 100).toFixed(0)}%)
                  </span>
                ))}
              </div>
              {detection.recommendations.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  {detection.recommendations.map((r, i) => <div key={i}>- {r}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report view */}
      {activeView === 'report' && report && (
        <div style={{ padding: 16, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Fairness Report</div>
            {report.overall_fairness_score !== null && (
              <span style={{
                fontSize: 13, padding: '4px 12px', borderRadius: 12, fontWeight: 600,
                background: report.overall_fairness_score >= 0.8 ? '#dcfce7' : report.overall_fairness_score >= 0.5 ? '#fef3c7' : '#fef2f2',
                color: report.overall_fairness_score >= 0.8 ? '#166534' : report.overall_fairness_score >= 0.5 ? '#92400e' : '#dc2626',
              }}>
                Overall Score: {fmtPct(report.overall_fairness_score, 0)}
              </span>
            )}
          </div>
          {report.attribute_results.map((ar, i) => (
            <div key={i} style={{ marginBottom: 12, padding: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{ar.attribute}</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                {ar.disparate_impact !== null && (
                  <span>Disparate Impact: <strong>{fmt(ar.disparate_impact, 3)}</strong></span>
                )}
                {ar.statistical_parity !== null && (
                  <span>Statistical Parity: <strong>{fmt(ar.statistical_parity, 3)}</strong></span>
                )}
              </div>
              {ar.group_sizes && Object.keys(ar.group_sizes).length > 0 && (
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                  Groups: {Object.entries(ar.group_sizes).map(([g, n]) => `${g} (${n})`).join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeView === 'report' && !report && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No report loaded. Run an evaluation or load an existing report.
        </div>
      )}

      {/* Model card view */}
      {activeView === 'model-card' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
            <input
              value={modelCardId} onChange={e => setModelCardId(e.target.value)}
              placeholder="Enter Model ID"
              style={{ flex: 1, maxWidth: 300, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
            />
            <button onClick={handleLoadModelCard} disabled={loading || !modelCardId} style={{
              fontSize: 13, padding: '6px 16px', borderRadius: 6, border: '1px solid var(--border)',
              fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
              opacity: (!modelCardId || loading) ? 0.5 : 1,
            }}>
              Load Card
            </button>
            <button onClick={handleGenerateCard} disabled={loading || !modelCardId} style={{
              fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none',
              fontWeight: 600, cursor: 'pointer', background: 'var(--accent-primary)', color: '#fff',
              opacity: (!modelCardId || loading) ? 0.5 : 1,
            }}>
              Generate Card
            </button>
          </div>

          {modelCard && (
            <div style={{ padding: 16, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong style={{ fontSize: 14 }}>{modelCard.model_name}</strong>
                {modelCardMd && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(['json', 'markdown'] as const).map(mode => (
                      <button key={mode} onClick={() => setCardViewMode(mode)} style={{
                        fontSize: 11, padding: '3px 10px', borderRadius: 4,
                        border: '1px solid var(--border)', cursor: 'pointer',
                        background: cardViewMode === mode ? 'var(--accent-primary)' : 'var(--bg-primary)',
                        color: cardViewMode === mode ? '#fff' : 'var(--text-primary)',
                      }}>
                        {mode.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {cardViewMode === 'json' ? (
                <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 400, background: 'var(--bg-primary)', padding: 12, borderRadius: 6 }}>
                  {JSON.stringify(modelCard, null, 2)}
                </pre>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg-primary)', padding: 12, borderRadius: 6 }}>
                  {modelCardMd}
                </div>
              )}
            </div>
          )}

          {!modelCard && !loading && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Enter a model ID to load or generate a model card.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FairnessPanel;
